<script setup>
import { Head, router } from '@inertiajs/vue3'
import { ref } from 'vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Card, CardHeader, CardTitle, CardContent } from '@/Components/ui/card'
import { CheckCircle, AlertCircle, ExternalLink, Loader2 } from 'lucide-vue-next'

const props = defineProps({
  connectAccountId: {
    type: String,
    default: null,
  },
  connectStatus: {
    type: Object,
    default: () => ({
      onboarding_status: 'pending',
      charges_enabled: false,
      payouts_enabled: false,
      onboarded_at: null,
    }),
  },
  expressLoginUrl: {
    type: String,
    default: null,
  },
  justReturned: {
    type: Boolean,
    default: false,
  },
})

const isLoading = ref(false)

const startOnboarding = () => {
  isLoading.value = true
  router.post('/teacher/connect/onboard', {}, {
    onFinish: () => {
      isLoading.value = false
    },
  })
}
</script>

<template>
  <Head title="Stripe Connect" />

  <DashboardLayout>
    <div class="w-full max-w-2xl space-y-6">
      <div class="space-y-0.5">
        <h2 class="text-2xl font-bold tracking-tight">Stripe Connect</h2>
        <p class="text-gray-500">Manage your payout account to receive earnings.</p>
      </div>

      <!-- Success: Onboarding Complete -->
      <Card v-if="connectStatus.onboarding_status === 'complete'" class="border-green-200 bg-green-50/50">
        <CardContent class="pt-6">
          <div class="flex items-start gap-4">
            <div class="p-3 bg-green-100 rounded-full shrink-0">
              <CheckCircle class="w-6 h-6 text-green-600" />
            </div>
            <div class="space-y-3">
              <div>
                <h3 class="text-lg font-semibold text-green-900">Payouts Active</h3>
                <p class="text-sm text-green-700 mt-1">
                  Your Stripe account is verified and payouts are enabled. Earnings will be deposited to your bank account automatically.
                </p>
              </div>

              <div class="flex flex-wrap items-center gap-3">
                <span v-if="connectStatus.payouts_enabled" class="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                  Payouts Enabled
                </span>
                <span v-if="connectStatus.charges_enabled" class="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                  Charges Enabled
                </span>
              </div>

              <a
                v-if="expressLoginUrl"
                :href="expressLoginUrl"
                target="_blank"
                class="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 transition-colors"
              >
                View Stripe Dashboard
                <ExternalLink class="w-4 h-4" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Pending: Account created but not verified -->
      <Card v-else-if="connectAccountId" class="border-amber-200 bg-amber-50/50">
        <CardContent class="pt-6">
          <div class="flex items-start gap-4">
            <div class="p-3 bg-amber-100 rounded-full shrink-0">
              <AlertCircle class="w-6 h-6 text-amber-600" />
            </div>
            <div class="space-y-3">
              <div>
                <h3 class="text-lg font-semibold text-amber-900">Verify Your Identity</h3>
                <p class="text-sm text-amber-700 mt-1">
                  Your Stripe account has been created but you need to verify your identity before you can receive payouts. This takes just a couple of minutes.
                </p>
              </div>

              <div v-if="justReturned" class="rounded-md bg-amber-100 p-3 text-sm text-amber-800">
                It looks like your verification isn't complete yet. Please click below to continue where you left off.
              </div>

              <button
                @click="startOnboarding"
                :disabled="isLoading"
                class="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 disabled:opacity-50 transition-colors"
              >
                <Loader2 v-if="isLoading" class="w-4 h-4 animate-spin" />
                {{ isLoading ? 'Redirecting...' : 'Verify Identity' }}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- No account: Set up payouts -->
      <Card v-else>
        <CardContent class="pt-6">
          <div class="flex items-start gap-4">
            <div class="p-3 bg-indigo-100 rounded-full shrink-0">
              <AlertCircle class="w-6 h-6 text-indigo-600" />
            </div>
            <div class="space-y-3">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Set Up Payouts</h3>
                <p class="text-sm text-gray-600 mt-1">
                  Connect your account to Stripe to receive automatic payouts to your bank account. Your details will be pre-filled to make this quick and easy.
                </p>
              </div>

              <button
                @click="startOnboarding"
                :disabled="isLoading"
                class="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 transition-colors"
              >
                <Loader2 v-if="isLoading" class="w-4 h-4 animate-spin" />
                {{ isLoading ? 'Redirecting...' : 'Set Up Payouts' }}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </DashboardLayout>
</template>
